import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

function CompleteRegister() {
  const [formData, setFormData] = useState({
    name: '',
    first_name: '',
    last_name: '',
    terms_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const { supabase, user } = useAuth();

  useEffect(() => {
    document.title = 'Register | Scucci Systems';
  }, []);

  useEffect(() => {
    if (!user) return;

    const metadata = user.user_metadata || {};
    const fullName = metadata.full_name || metadata.name || '';
    const [firstName = '', ...lastNameParts] = fullName.split(' ').filter(Boolean);

    setFormData((current) => ({
      ...current,
      first_name: current.first_name || metadata.first_name || metadata.given_name || firstName,
      last_name: current.last_name || metadata.last_name || metadata.family_name || lastNameParts.join(' '),
    }));
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const businessName = formData.name.trim();
    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();

    if (!businessName || !firstName || !lastName) {
      setError('Please complete all required fields.');
      setLoading(false);
      return;
    }

    if (!formData.terms_accepted) {
      setError('You must agree to the Terms & Privacy to register.');
      setLoading(false);
      return;
    }

    if (!user) {
      setError('Your Google session was not found. Please sign in again.');
      setLoading(false);
      return;
    }

    try {
      const { data, error: rpcError } = await supabase.schema('clients').rpc('manual_create_client_profile', {
        p_user_id: user.id,
        p_email: user.email || '',
        p_name: businessName,
        p_first_name: firstName,
        p_last_name: lastName,
        p_phone: '',
        p_address1: '',
        p_address2: '',
        p_city: '',
        p_state: '',
        p_zip: '',
        p_business_type: 'estate_sales',
        p_terms_accepted: formData.terms_accepted,
        p_accepted_at: new Date().toISOString(),
      });

      if (rpcError) throw rpcError;

      console.log('Profile created:', data);
      setSuccess('Registration successful! Redirecting to membership setup...');
      setTimeout(() => navigate('/estate-sales-pipeline/member-maint2'), 2000);
    } catch (err) {
      setError(err.message || 'Profile creation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="absolute top-4 right-4">
        <img src="/images/scucci-core-icon.png" alt="Scucci Systems Logo" className="h-10 w-10" />
      </div>
      <div className="flex-grow p-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-2 text-gray-800">Scucci Systems</h1>
          <h2 className="text-2xl font-semibold mb-3 text-gray-700">Estate Sale Business Platform</h2>
          <p className="max-w-xl mx-auto text-gray-600 mb-4">
            Create inventory from photos, print price tags, manage checkout, and track sale results in one place.
          </p>
        </div>
        <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-2 text-gray-700">Complete Registration</h2>
          <p className="text-gray-600 mb-4">
            We just need a few details to finish setting up your estate sale account.
          </p>
          {success && <p className="text-green-500 mb-4">{success}</p>}
          {error && <p className="text-red-500 mb-4">{error}</p>}
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block mb-1 text-gray-700">Business Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-gray-700">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-gray-700">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div className="flex items-start">
              <input
                type="checkbox"
                name="terms_accepted"
                checked={formData.terms_accepted}
                onChange={handleChange}
                className="mt-1 h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300 rounded"
                required
              />
              <label className="ml-2 text-gray-700">
                I agree to the&nbsp;
                <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                  Scucci Terms of Service, Privacy Policy, and Refund Policy
                </a>.
              </label>
            </div>
            <button
              type="submit"
              className="w-full bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Completing Registration...' : 'Complete Registration'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full mt-4 text-blue-500 hover:underline"
            >
              Back to Home
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CompleteRegister;
